import React, { useEffect, useState } from "react";
import { observer, useLocalStore, inject } from "mobx-react";
import { useParams, useRouteMatch } from "react-router-dom";
import styled from "styled-components";

import TabBar from "../TabBar/TabBar";
import Container from "../list/Container";
import Header from "../Header/Header";
import { PackStoreContext } from "./PackStore";
import NotFound from "../common/ErrorMessages";

import FloatingChatButton from "app/Chat/buttons/FloatingChatButton";

// const { default: Header } = require("../Header/Header");
// const appStore = require("../AppStore").default;

import ScheduleCall from "app/OnboardingCall/ScheduleCall";
import StickyBanner from "app/OnboardingCall/StickyBanner";
import ApiClient from "utils/ApiClient";

const BANNER_HEIGHT = 48;
const POPUP_DELAY_MS = 120000;

function Pack({ store, packLists, dialogsToDom, userStore, assistantStore, onBoardingStore }) {
  const match = useRouteMatch("/pack/:id/list/:listId");
  const currentListId = match?.params?.listId;
  const { id } = useParams();
  const dashboard = store.pack?.is_dashboard_enabled === '1' ? { name: "Dashboard" , id: "dashboard" } : '';

  const [isTimeUp, setIsTimeUp] = useState(false);
  const selectedSheet = currentListId != "dashboard" ? match
    ? packLists.find((list) => list.id == match.params.listId)
    : ""
    : dashboard;

  useEffect(() => {
    store.handlePackIdChange(id);
    if (match) {
      store.handleCurrentListIdChange(match.params.listId);
    } else {
      store.openFirstSheet();
    }
  }, [id, match, store, store.lists]);

  useEffect(() => {
    // If user is not admin or owner, hide the assistant chat button
    if (
      !userStore.user ||
      !(userStore.user.isAdminForUsers || userStore.user.isOwner) ||
      id === "system" ||
      store.pack?.is_log_pack == "1" ||
      selectedSheet?.is_log_sheet == "1"
    ) {
      assistantStore.hideAssistantButton();
      return;
    }

    assistantStore.showAssistantButton();
  }, [userStore.user, selectedSheet, store.pack, id]);

  useEffect(() => {
    if (userStore.user?.is_onboarding_prompt_visible) {
      const timer = setTimeout(() => {
        setIsTimeUp(true);
      }, POPUP_DELAY_MS);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [userStore.user?.is_onboarding_prompt_visible]);

  useEffect(() => {
    const shouldShow =
      userStore.user?.is_onboarding_prompt_visible &&
      !onBoardingStore.dialogOpened &&
      !assistantStore.opened;
  
    if (!shouldShow) return;

    if (!onBoardingStore.isScheduleCallBannerOpened) {
      onBoardingStore.openScheduleCallBanner();
    }
  
    if (isTimeUp) {
      onBoardingStore.openScheduleCallDialog();
      setIsTimeUp(false);
    }
  }, [
    isTimeUp,
    userStore.user?.is_onboarding_prompt_visible,
    onBoardingStore.dialogOpened,
    assistantStore.opened
  ]);
  const handleChatButtonClick = () => {
    assistantStore.openChat();
  };

  const handleScheduleCallCloseAll = async () => {
    if(onBoardingStore.scheduleCallDialogOpened) {
      onBoardingStore.closeScheduleCallDialog();
    }
    if(onBoardingStore.isScheduleCallBannerOpened) {
      onBoardingStore.closeScheduleCallBanner();
    }
    if(onBoardingStore.isScheduleCallonMenuOpened) {
      onBoardingStore.closeScheduleCallOnMenu();
    }
  };

  const handleScheduleCallClose = () => {
    onBoardingStore.closeScheduleCallDialog();
  };

  const handleBannerClose = async () => {
    onBoardingStore.closeScheduleCallBanner();
    if(userStore.user.is_onboarding_prompt_visible) {
      onBoardingStore.openScheduleCallOnMenu();
      const resp = await ApiClient.Account.updateOnboardingPrompt({
        user_id: window.appStore.userStore.user.user_id,
        prompt_value: 0,
      });
      if(resp.success) {
        window.appStore.userStore.user.is_onboarding_prompt_visible = false;
      }
    }
  };

  if (!store.isValidPack) {
    return <NotFound />;
  }

  return (
    <PackStoreContext.Provider value={store}>
      <div
        style={{
          height: onBoardingStore.isScheduleCallBannerOpened ? `calc(100vh - ${BANNER_HEIGHT}px)` : "100vh",
          marginTop: onBoardingStore.isScheduleCallBannerOpened ? `${BANNER_HEIGHT}px` : "0px",
          transition: "all 0.3s ease-in-out",
          display: "flex",
          flexFlow: "column",
          overflow: "hidden",
          flexGrow: 1,
        }}
      >
        <Header
          packName={store.packName}
          changePackName={store.handlePackNameChange}
          themeColor={store.themeColor}
          changeThemeColor={store.handleThemeColorChange}
          icon={store.icon}
          changeIcon={store.handleIconChange}
          isScheduleCallonMenuOpened={onBoardingStore.isScheduleCallonMenuOpened}
        />
        <TabBar
          sheets={dashboard !== '' ? [dashboard, ...packLists] : packLists}
          selectedSheet={selectedSheet}
          renameSheet={async (value) =>
            store.handleRenameSheet(value, selectedSheet)
          }
          themeColor={store.themeColor}
          changeSheet={store.handleChangeSheet}
          onAddSheetBtnClick={store.handleCreateSheet}
          deleteSheet={store.handleDeleteSheet}
          openLogSheet={store.openLogSheet}
          isSheetCreating={store.isSheetCreating}
          duplicateSheet={store.handleDuplicateSheet}
        />
        {currentListId != "dashboard" ? (
          store.lists.map((list) => (
            <ContainerWrapper
              key={list.id}
              themeColor={store.themeColor}
              listId={list.id}
              value={currentListId}
            />
          ))
        ) : (
          <ContainerWrapper
            themeColor={store.themeColor}
            listId={currentListId}
            value={currentListId}
          />
        )}
        {store.lists.length === 0 && !store.isListsLoading && (
          <NoSheetMessage>
            No sheets. To create a sheet click on Add Sheet.
          </NoSheetMessage>
        )}
      </div>
      {assistantStore.isAssistantButtonVisible && (
        <FloatingChatButton onClick={handleChatButtonClick} />
      )}

      <ScheduleCall
        open={onBoardingStore.scheduleCallDialogOpened}
        onClose={handleScheduleCallClose}
        handleScheduleCallCloseAll={handleScheduleCallCloseAll}
      />
      
      <StickyBanner 
        open={onBoardingStore.isScheduleCallBannerOpened} 
        onClose={handleBannerClose} 
        handleScheduleCallCloseAll={handleScheduleCallCloseAll}
      />

    </PackStoreContext.Provider>
  );
}

function ContainerWrapper(props) {
  const { children, value, listId, themeColor, ...other } = props;
  const [isVisited, setIsVisited] = React.useState(false);

  if (value === listId && !isVisited) {
    setIsVisited(true);
  }

  return (
    <StyledDiv
      role="tabpanel"
      hidden={value !== listId}
      {...other}
      style={{ width: "auto", height: "auto" }}
      key={listId}
    >
      {isVisited && (
        <Container key={listId} themeColor={themeColor} listId={listId} />
      )}
    </StyledDiv>
  );
}

const StyledDiv = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const NoSheetMessage = styled.div`
  width: 100%;
  text-align: center;
  opacity: 0.6;
  margin-top: 100px;
`;

export default inject(({ store: appStore }) => ({
  store: appStore.packStore,
  packLists: appStore.leftHoverMenuStore.packLists,
  chatStore: appStore.chatStore,
  assistantStore: appStore.assistantStore,
  userStore: appStore.userStore,
  onBoardingStore: appStore.onBoardingStore,
}))(observer(Pack));
